import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { ChevronDown, ChevronUp, Sparkles } from 'lucide-react';

import css from './OnboardingChecklist.module.css';

const StepIcon = ({ status, index }) => {
  if (status === 'done') {
    return (
      <span className={css.iconDone}>
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path d="M2 6l3 3 5-5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
    );
  }
  if (status === 'active') {
    return (
      <span className={css.iconActive}>{index}</span>
    );
  }
  return <span className={css.iconLocked}>{index}</span>;
};

const renderStep = (step, index) => {
  if (step.status === 'done') {
    return (
      <li key={step.id} className={css.floatingStepDone}>
        <StepIcon status="done" index={index} />
        <span className={css.floatingStepTitleDone}>{step.title}</span>
      </li>
    );
  }

  if (step.status === 'active') {
    return (
      <li key={step.id} className={css.floatingStepActive}>
        <div className={css.floatingStepActiveInner}>
          <StepIcon status="active" index={index} />
          <div className={css.floatingStepActiveText}>
            <p className={css.floatingStepActiveTitle}>{step.title}</p>
            {step.description && (
              <p className={css.floatingStepActiveDesc}>{step.description}</p>
            )}
          </div>
        </div>
        {step.isInProgress ? null : step.onAction ? (
          <button className={css.floatingStepStartBtn} onClick={step.onAction}>
            Start <span aria-hidden>→</span>
          </button>
        ) : (
          <span className={css.floatingStepStartLabel}>Start →</span>
        )}
      </li>
    );
  }

  return (
    <li key={step.id} className={css.floatingStepLocked}>
      <StepIcon status="locked" index={index} />
      <span className={css.floatingStepTitleLocked}>{step.title}</span>
      <span className={css.floatingStepLockedLabel}>LOCKED</span>
    </li>
  );
};

/**
 * OnboardingChecklist
 *
 * @component
 * @param {Object} props
 * @param {string?} props.rootClassName
 * @param {string?} props.className
 * @param {'large'|'floating'} props.variant
 * @param {Array} props.steps
 * @param {boolean} props.isMinified when variant=floating, collapse to pill
 * @param {Function} props.onToggleMinified
 */
const OnboardingChecklist = props => {
  const { variant, steps, isMinified, onToggleMinified, rootClassName, className } = props;

  const completedCount = steps.filter(s => s.status === 'done').length;
  const progressPct = steps.length > 0 ? (completedCount / steps.length) * 100 : 0;

  if (variant === 'floating' && isMinified) {
    const classes = classNames(rootClassName || css.minified, className);
    const radius = 15;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference * (1 - progressPct / 100);
    return (
      <div
        className={classes}
        onClick={onToggleMinified}
        role="button"
        tabIndex={0}
        onKeyDown={e => e.key === 'Enter' && onToggleMinified?.()}
      >
        <svg className={css.minifiedRing} width="40" height="40" viewBox="0 0 40 40">
          <circle cx="20" cy="20" r={radius} className={css.minifiedRingBg} />
          <circle
            cx="20" cy="20" r={radius}
            className={css.minifiedRingFill}
            strokeDasharray={circumference}
            strokeDashoffset={offset}
          />
          <text x="20" y="20" className={css.minifiedRingText} dominantBaseline="central" textAnchor="middle">
            {Math.round(progressPct)}
          </text>
        </svg>
        <div className={css.minifiedText}>
          <span className={css.minifiedLabel}>{steps.find(s => s.status === 'active')?.title ?? 'Your onboarding checklist'}</span>
          <span className={css.minifiedCount}>{completedCount} of {steps.length} done</span>
        </div>
        <ChevronUp size={16} className={css.minifiedChevron} />
      </div>
    );
  }

  const isFloating = variant === 'floating';
  const rootClass = isFloating ? css.floating : css.large;
  const classes = classNames(rootClassName || rootClass, className);

  return (
    <div className={classes}>
      <div className={css.floatingTop}>
        <div className={css.floatingHeader}>
          <div className={css.floatingHeaderLeft}>
            <div className={css.floatingHeaderIcon}>
              <Sparkles size={18} strokeWidth={2} />
            </div>
            <div>
              <p className={css.title}>Your onboarding checklist</p>
              <p className={css.floatingProgress}>{completedCount} of {steps.length} complete</p>
            </div>
          </div>
          {isFloating && (
            <button
              className={css.chevronButton}
              onClick={onToggleMinified}
              aria-label="Minimise onboarding checklist"
            >
              <ChevronDown size={16} />
            </button>
          )}
        </div>
      </div>

      <ol className={css.floatingStepList}>
        {steps.map((step, i) => renderStep(step, i + 1))}
      </ol>
    </div>
  );
};

OnboardingChecklist.defaultProps = {
  variant: 'large',
  steps: [],
  isMinified: false,
  onToggleMinified: null,
  rootClassName: null,
  className: null,
};

OnboardingChecklist.propTypes = {
  variant: PropTypes.oneOf(['large', 'floating']),
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      description: PropTypes.string,
      status: PropTypes.oneOf(['done', 'active', 'locked']).isRequired,
      isInProgress: PropTypes.bool,
      onAction: PropTypes.func,
    })
  ),
  isMinified: PropTypes.bool,
  onToggleMinified: PropTypes.func,
  rootClassName: PropTypes.string,
  className: PropTypes.string,
};

export default OnboardingChecklist;
